import { FieldValues, Path, useFormContext } from "react-hook-form";
import { FormDescription, FormItem } from "./form";
import { Input } from "alurkerja-ui";
import { RequiredAsterisk } from "./required-asterisk";
import { useId, useState } from "react";
import { MediaData, useMediaDownload, useMediaTempCreate } from "@/api/media";
import { swalError } from "@/utils/lib/swal";
import { AxiosError } from "axios";
import { DownloadButton } from "../ui/button";

interface FieldFileProps<TFormValues extends FieldValues> {
  name: Path<TFormValues>;
  label: string;
  hideLabel?: boolean;
  description?: string;
  accept?: string;
  isDisabled?: boolean;
  isVisuallyHidden?: boolean;
  hasRequiredAsterisk?: boolean;
  title?: string;
  defaultName?: string;
  multiple?: boolean;
  media?: {
    target: string;
    entityId?: string;
    className?: string;
    type?: string;
    before?: (files: FileList) => File | Promise<File> | undefined;
    after?: (mediaData: MediaData) => void;
  };
  canDownloadExistingFile?: boolean;
  pathExistingFile?: string;
  uploadApiUrl?: string;
}
export function FieldFile<TFormValues extends FieldValues>({
  name,
  label,
  hideLabel,
  description,
  accept,
  isDisabled = false,
  isVisuallyHidden = false,
  hasRequiredAsterisk = false,
  title,
  defaultName,
  multiple = false,
  media,
  canDownloadExistingFile,
  pathExistingFile,
  uploadApiUrl = undefined,
}: FieldFileProps<TFormValues>) {
  const [mediaName, setMediaName] = useState("");
  const [isErrorFileType, setIsErrorFileType] = useState(false);

  const { mutate, isPending } = useMediaTempCreate();
  const { mutate: downloadDocument, isPending: isPendingDownload } =
    useMediaDownload();

  const labelId = useId();
  const form = useFormContext();
  const files = form.watch(name);
  const fileNames = files
    ? Array.from(files as FileList)
        .map((file) => file.name)
        .join(", ")
    : null;
  const fieldError = form.formState.errors[name];
  const registered = form.register(name);

  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const input = e.currentTarget.previousElementSibling as HTMLInputElement;
    input.click();
  }

  async function handleAutoMedia(e: React.ChangeEvent<HTMLInputElement>) {
    if (!media || !e.target.files?.length) {
      return;
    }
    const inputEl = e.target;
    const beforeResult = await media.before?.(e.target.files);
    if (media.before && !beforeResult) {
      return;
    }

    mutate(
      {
        data: {
          file: media.before && beforeResult ? beforeResult : e.target.files[0],
          target: media.target,
          entityId: media.entityId,
          className: media.className,
          type: media.type,
        },
        uploadApiUrl,
      },
      {
        onSuccess: (result) => {
          // @ts-expect-error result has MediaData type
          form.setValue(name, result);
          form.clearErrors(name);

          setMediaName(result.originalFileName);
          media.after?.(result);
        },
        onError: (result: Error) => {
          const err = result as AxiosError;
          const errorMessage = (err.response?.data as { data: unknown })?.data;

          inputEl.value = "";

          swalError.fire({
            title: "Media Upload failed",
            text: `${errorMessage}`,
          });
        },
      },
    );
  }

  // function validation allowed filetype
  function validateFileType(e: React.ChangeEvent<HTMLInputElement>): boolean {
    if (e.target.files === null) {
      return false;
    }

    // get extension file from filename
    const file = e.target.files[0];
    const fileType = file.name.split(".").pop()?.toLowerCase();

    const validExtensions = accept
      ?.split(",")
      .map((ext) => ext.trim().toLowerCase());
    if (!validExtensions?.includes(`.${fileType as string}`)) {
      form.setError(name, {
        type: "file-type",
        message: `Please upload files in ${accept} format only`,
      });
      setIsErrorFileType(true);
      e.target.value = "";
      return false;
    }

    setIsErrorFileType(false);
    return true;
  }

  const displayText =
    fileNames || mediaName || defaultName || "No file choosen";

  function handleDownloadDocument(path: string) {
    downloadDocument(
      {
        path,
        fileName: path.split("/").pop() || "downloaded_file",
      },
      {
        onError: () => {
          swalError.fire({
            text: "Failed to download file",
          });
        },
      },
    );
  }

  return (
    <FormItem
      className={`${
        isVisuallyHidden ? "sr-only" : "break-inside-avoid-column"
      }`}
    >
      <label
        id={labelId}
        className={isDisabled ? " opacity-70" : ""}
        title={title}
      >
        <div
          className={`${
            hideLabel
              ? "sr-only"
              : "mb-2 text-sm font-medium leading-none dark:text-neutral-300"
          }${
            fieldError || isErrorFileType
              ? " text-red-500 dark:text-red-900"
              : ""
          }`}
        >
          {label}
          {hasRequiredAsterisk && <RequiredAsterisk />}
        </div>
        <div className="flex items-center gap-1">
          <Input
            {...registered}
            type="file"
            accept={accept}
            disabled={isDisabled}
            className="hidden"
            aria-labelledby={labelId}
            multiple={multiple}
            onChange={(e) => {
              let isTypeValid = true;
              if (accept) {
                isTypeValid = validateFileType(e);
              }
              if (!isTypeValid) {
                return;
              }

              if (media) {
                handleAutoMedia(e);
              } else {
                registered.onChange(e);
              }
            }}
          />
          <button
            type="button"
            className="input h-min-11 flex items-center gap-x-2 border border-[#c4c4c480] bg-white focus-within:border-indigo-600 focus-within:ring-indigo-600 focus:border-indigo-600 focus:ring-indigo-600"
            onClick={handleClick}
            disabled={isDisabled || isPending}
          >
            <span className="rounded bg-neutral-200 px-2 py-1 text-sm dark:bg-neutral-500">
              {isPending ? "Loading..." : "Browse"}
            </span>
            <span className="overflow-x-hidden whitespace-nowrap text-neutral-950">
              {displayText}
            </span>
          </button>

          {canDownloadExistingFile && pathExistingFile && (
            <DownloadButton
              type="button"
              className="flex aspect-square items-center justify-center !p-2"
              loading={isPendingDownload}
              onClick={() => {
                if (pathExistingFile) {
                  handleDownloadDocument(pathExistingFile);
                }
              }}
            />
          )}
        </div>
      </label>
      {description && <FormDescription>{description}</FormDescription>}
      {(form.formState.errors[name] || isErrorFileType) && (
        <span className="text-sm font-medium text-red-500 dark:text-red-900">
          {fieldError?.message && String(fieldError?.message)}
          {isErrorFileType && `Please upload files in ${accept} format only`}
        </span>
      )}
    </FormItem>
  );
}
